/**
 * StorageAdapter on Supabase (Postgres via PostgREST). Extends BaseStorage, so events, export, booking and move come from the
 * same code as the IndexedDB/memory adapters; only the transaction primitive differs:
 *
 *   transact(fn) = run fn against live reads (buffering writes) -> commit with ONE rpc('apply_batch') whose preconditions say
 *   "every row I read and am about to overwrite still has the updated_at I saw". If another writer got there first the database
 *   raises 40001 and fn is re-run on fresh data (up to 4 times). Rows that were only listed (phantoms) are not checked: the
 *   things that need that guarantee are protected by constraints instead (appointments: exclusion constraint; waitlist: partial
 *   unique index "one accepted offer per slot").
 */
import { applyFilter } from '../storage/query';
import { BaseStorage, type Change, type Executed } from '../storage/base';
import type { ListFilter, Tx } from '../../domain/tx';
import { ENTITY_NAMES, type ConsentRecord, type EntityMap, type EntityName, type Id } from '../../domain/types';
import type { BookingDraft, BookResult, MoveRequest, MoveResult } from '../../domain/booking';
import type { ChangeEvent, Tables } from '../types';
import { businessIdOf, consentEvents, fromRow, selectList, TABLE, toQuery, toRow, WRITE_TABLE, type MapCtx } from './mappers';
import { RestError, type Rest, type Row } from './rest';

export const PHOTO_BUCKET = 'brand-photos';

interface Op { key: string; entity: EntityName; id: Id; kind: 'put' | 'delete'; table: string; rows: Row[]; businessId: string; value?: unknown }
const keyOf = (entity: EntityName, id: Id): string => `${entity}:${id}`;
const clone = <T>(v: T): T => structuredClone(v);

/** A slot that looked free was taken between the check and the commit (SQLSTATE 23P01 from the exclusion constraint). */
export class SlotTakenError extends Error { constructor() { super('slot_taken'); this.name = 'SlotTakenError'; } }

export interface SupabaseStorageOptions {
  rest: Rest;
  ctx: MapCtx;
  maxRetries?: number;
  /** Poll interval for the Realtime-less change feed (ms). 0 = off (default). */
  pollMs?: number;
  /** The change feed reads a signed-in-only RPC; when this says false the poll is skipped (anonymous visitors). */
  signedIn?: () => boolean;
}

const TABLE_TO_ENTITY: Record<string, EntityName> = {
  businesses: 'businesses', staff: 'staff', services: 'services', weekly_hours: 'weeklyHours', time_off: 'timeOff', clients: 'clients',
  appointments: 'appointments', waitlist_entries: 'waitlistEntries', waitlist_offers: 'waitlistOffers', messages: 'messages',
  payments: 'payments', settings: 'settings',
};

export class SupabaseStorage extends BaseStorage {
  readonly kind = 'supabase' as const;
  private readonly rest: Rest;
  private readonly ctx: MapCtx;
  private readonly maxRetries: number;
  private readonly signedIn: (() => boolean) | undefined;
  private timer: ReturnType<typeof setInterval> | undefined;
  private versions: Record<string, string> = {};
  /** Signature of the photos / aliases we last saw per business, so unchanged business writes skip the side tables. */
  private readonly sideSig = new Map<string, string>();

  constructor(opts: SupabaseStorageOptions) {
    super(undefined);
    this.rest = opts.rest;
    this.ctx = opts.ctx;
    this.maxRetries = opts.maxRetries ?? 4;
    this.signedIn = opts.signedIn;
    if (opts.pollMs && opts.pollMs > 0) this.startPolling(opts.pollMs);
  }

  // ------------------------------------------------------------------ reads
  private async fetch<E extends EntityName>(entity: E, filter: ListFilter<EntityMap[E]> | undefined, id?: Id): Promise<EntityMap[E][]> {
    const q = toQuery(entity, filter, this.ctx);
    const eq = id === undefined ? q.eq : { ...q.eq, id };
    const rows = await this.rest.select(TABLE[entity], {
      select: selectList(entity, this.ctx), eq, ...(Object.keys(q.gte).length ? { gte: q.gte } : {}), ...(Object.keys(q.lt).length ? { lt: q.lt } : {}),
      ...(filter?.limit !== undefined && !q.residual ? { limit: filter.limit, order: 'id' } : {}),
    });
    const mapped = rows.map((r) => fromRow(entity, r, this.ctx));
    if (entity === 'businesses') for (const b of mapped as EntityMap['businesses'][]) this.sideSig.set(b.id, this.signature(b));
    return mapped;
  }

  private signature(b: EntityMap['businesses']): string {
    return JSON.stringify([(b.brand.photos ?? []).map((p) => [p.id, p.order, p.alt ?? '', p.dataUrl.startsWith('data:') ? 'new' : 'same']), b.slugAliases ?? []]);
  }

  protected async execute<R>(_entities: readonly EntityName[], mode: 'r' | 'rw', fn: (tx: Tx) => Promise<R>): Promise<Executed<R>> {
    for (let attempt = 0; ; attempt++) {
      const reads = new Map<string, string | null>();
      const writes = new Map<string, Op>();
      const tx: Tx = {
        get: async (entity, id) => {
          const k = keyOf(entity, id);
          const w = writes.get(k);
          if (w) return w.kind === 'put' ? clone(w.value) as never : undefined;
          const [row] = await this.fetch(entity, undefined, id);
          reads.set(k, row ? row.updatedAt : null);
          return row as never;
        },
        list: async (entity, filter) => {
          const fetched = (await this.fetch(entity, filter as never)) as Array<{ id: Id; updatedAt: string }>;
          const byId = new Map(fetched.map((r) => [r.id, r]));
          for (const r of fetched) reads.set(keyOf(entity, r.id), r.updatedAt);
          for (const w of writes.values()) {
            if (w.entity !== entity) continue;
            if (w.kind === 'put') byId.set(w.id, clone(w.value) as never); else byId.delete(w.id);
          }
          return applyFilter([...byId.values()], filter as never).map(clone) as never;
        },
        put: async (entity, row) => {
          if (mode === 'r') throw new Error('read-only transaction');
          const businessId = businessIdOf(entity, row as { id: string; businessId?: string });
          const rows = entity === 'consents' ? consentEvents(row as ConsentRecord) : [toRow(entity, row as never, this.ctx)];
          writes.set(keyOf(entity, row.id), { key: keyOf(entity, row.id), entity, id: row.id, kind: 'put', table: WRITE_TABLE[entity], rows, businessId, value: clone(row) });
          return row;
        },
        delete: async (entity, id) => {
          if (mode === 'r') throw new Error('read-only transaction');
          if (entity === 'consents') throw new Error('consents are an append-only ledger: revoke instead of deleting');
          // the business id is needed for apply_batch: take it from a buffered write, or read the row (this also records a precondition)
          const k = keyOf(entity, id);
          let businessId = writes.get(k)?.businessId;
          if (!businessId) {
            const [row] = await this.fetch(entity, undefined, id);
            if (!row) return;                                  // deleting a missing row is a no-op, like the other adapters
            reads.set(k, row.updatedAt);
            businessId = businessIdOf(entity, row as { id: string; businessId?: string });
          }
          writes.set(k, { key: k, entity, id, kind: 'delete', table: WRITE_TABLE[entity], rows: [], businessId });
        },
      };
      const value = await fn(tx);
      const ops = [...writes.values()];
      if (ops.length === 0) return { value, changes: [] };
      try {
        await this.commit(ops, reads);
      } catch (e) {
        if (e instanceof RestError && e.isSlotTaken) throw new SlotTakenError();
        if (e instanceof RestError && e.isConflict && attempt < this.maxRetries) continue;
        throw e;
      }
      const changes: Change[] = ops.map((o) => ({ entity: o.entity, type: o.kind, id: o.id }));
      return { value, changes };
    }
  }

  private async commit(ops: Op[], reads: Map<string, string | null>): Promise<void> {
    const ids = new Set(ops.map((o) => o.businessId).filter(Boolean));
    if (ids.size !== 1) throw new Error(`a transaction must touch exactly one business (got ${ids.size})`);
    const businessId = [...ids][0] as string;
    const checks = ops.filter((o) => o.entity !== 'consents' && reads.has(o.key)).map((o) => ({ table: o.table, id: o.id, updated_at: reads.get(o.key) ?? null }));
    const batch: Array<Record<string, unknown>> = ops.flatMap((o): Array<Record<string, unknown>> => (o.kind === 'delete' ? [{ op: 'delete', table: o.table, id: o.id }] : o.rows.map((row) => ({ op: 'put', table: o.table, row }))));
    await this.rest.rpc('apply_batch', { p_business_id: businessId, p_checks: checks, p_ops: batch });
    for (const o of ops) if (o.entity === 'businesses' && o.kind === 'put') await this.syncBusinessSides(o.value as EntityMap['businesses']);
  }

  // ------------------------------------------------------------------ brand photos (Storage) and slug aliases (side tables)
  private async syncBusinessSides(b: EntityMap['businesses']): Promise<void> {
    const sig = this.signature(b);
    if (this.sideSig.get(b.id) === sig) return;
    const photos = b.brand.photos ?? [];
    const existing = await this.rest.select('brand_photos', { select: 'id,storage_path', eq: { business_id: b.id } });
    const keep = new Set(photos.map((p) => p.id));
    const gone = existing.filter((r) => !keep.has(r.id as string));
    for (const p of photos) {
      if (p.dataUrl.startsWith('data:')) {
        const img = decodeDataUrl(p.dataUrl);
        const path = `${b.id}/${p.id}.${img.ext}`;
        await this.rest.storage.upload(PHOTO_BUCKET, path, img.bytes, img.mime);
        await this.rest.upsert('brand_photos', [{ id: p.id, business_id: b.id, storage_path: path, alt: p.alt ?? null, sort_order: p.order, bytes: img.bytes.byteLength }]);
      } else {
        await this.rest.upsert('brand_photos', [{ id: p.id, business_id: b.id, alt: p.alt ?? null, sort_order: p.order }]);
      }
    }
    if (gone.length > 0) {
      await this.rest.storage.remove(PHOTO_BUCKET, gone.map((r) => r.storage_path as string));
      for (const r of gone) await this.rest.remove('brand_photos', { id: r.id as string });
    }
    const wantAliases = b.slugAliases ?? [];
    const haveAliases = await this.rest.select('slug_aliases', { select: 'slug', eq: { business_id: b.id }, order: 'slug' });
    await this.rest.upsert('slug_aliases', wantAliases.map((a) => ({ slug: a.slug, business_id: b.id, until: a.until })), { onConflict: 'slug' });
    for (const r of haveAliases) if (!wantAliases.some((a) => a.slug === r.slug)) await this.rest.remove('slug_aliases', { slug: r.slug as string });
    this.sideSig.set(b.id, sig);
  }

  // ------------------------------------------------------------------ booking: constraint races become a clean "conflict"
  override async bookAppointment(draft: BookingDraft, now: string): Promise<BookResult> {
    try { return await super.bookAppointment(draft, now); } catch (e) {
      if (!(e instanceof SlotTakenError)) throw e;
      try { return await super.bookAppointment(draft, now); } catch (e2) {
        if (e2 instanceof SlotTakenError) return { ok: false, reason: 'conflict', nextSlots: [] };
        throw e2;
      }
    }
  }
  override async moveAppointment(req: MoveRequest, now: string): Promise<MoveResult> {
    try { return await super.moveAppointment(req, now); } catch (e) {
      if (!(e instanceof SlotTakenError)) throw e;
      try { return await super.moveAppointment(req, now); } catch (e2) {
        if (e2 instanceof SlotTakenError) return { ok: false, reason: 'conflict', nextSlots: [] };
        throw e2;
      }
    }
  }

  // ------------------------------------------------------------------ change feed (polling; swap for Realtime later)
  /** Compares per-table versions (count + max updated_at) and announces `reset` for tables that moved. Returns the entities that changed. */
  async pollOnce(): Promise<EntityName[]> {
    if (this.signedIn && !this.signedIn()) return [];   // table_versions is for signed-in users only; anonymous visitors would get a 401
    const v = await this.rest.rpc<Record<string, { n: number; v: string | null }>>('table_versions');
    const changed: EntityName[] = [];
    const next: Record<string, string> = {};
    for (const [table, info] of Object.entries(v)) {
      const sig = `${info.n}:${info.v ?? ''}`;
      next[table] = sig;
      const entity = TABLE_TO_ENTITY[table];
      if (entity && table in this.versions && this.versions[table] !== sig) changed.push(entity);
    }
    this.versions = next;
    if (changed.length > 0) this.emit(changed.map((entity): ChangeEvent => ({ entity, type: 'reset', remote: true })), false);
    return changed;
  }
  startPolling(ms: number): void {
    this.stopPolling();
    this.timer = setInterval(() => void this.pollOnce().catch(() => undefined), ms);
  }
  stopPolling(): void { if (this.timer) clearInterval(this.timer); this.timer = undefined; }

  // ------------------------------------------------------------------ dump / reset
  protected async dumpAll(): Promise<Tables> {
    const out = {} as Record<EntityName, unknown[]>;
    for (const name of ENTITY_NAMES) out[name] = (await this.fetch(name, undefined)) as unknown[];
    return out as unknown as Tables;
  }
  protected async replaceAll(): Promise<void> {
    throw new Error('reset/import is demo-only on the Supabase backend (use the import_dump function)');
  }
  protected closeImpl(): void { this.stopPolling(); }
}

export function decodeDataUrl(dataUrl: string): { mime: string; ext: string; bytes: Uint8Array } {
  const m = /^data:([a-z]+\/[a-z0-9.+-]+);base64,(.*)$/i.exec(dataUrl);
  if (!m) throw new Error('invalid_image');
  const mime = m[1]!.toLowerCase();
  const ext = mime === 'image/jpeg' ? 'jpg' : mime === 'image/png' ? 'png' : mime === 'image/webp' ? 'webp' : '';
  if (!ext) throw new Error(`unsupported image type ${mime} (jpeg, png, webp)`);
  const bin = atob(m[2]!);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return { mime, ext, bytes };
}
